/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal } from "semantic-ui-react";
import { TextField } from "@mui/material";
import { allFhirResourceTypes } from "../dictionaries/CodeSystemLookup";
import getSearchResults from "../functions/getSearchResultsAPI";
import { selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import CodeEntry from "./CodeEntry";
import ReferenceEntry, { createNewReferencedResource, SelectProfileForNewResource } from "./ReferenceEntry";
import StringEntry from "./StringEntry";
import CodeableConceptEntry from "./CodeableConceptEntry";
import CodingEntry, { CodingEntryWithSystemSelector } from "./CodingEntry";
import AnnotationEntry from "./AnnotationEntry";
import ContactDetailEntry, { ContactPointEntry } from "./ContactDetailEntry";
import IdentifierEntry from "./IdentifierEntry";
import PeriodEntry, { DateEntry, DateTimeEntry } from "./PeriodEntry";
import PositiveIntEntry from "./PositiveIntEntry";
import QuantityEntry from "./QuantityEntry";
import RangeEntry from "./RangeEntry";
import UriEntry from "./UriEntry";
import UsageContextEntry from "./UsageContextEntry";
import AttachmentEntry from "./AttachmentEntry";
import AttesterEntry from "./AttesterEntry";
import DecimalEntry from "./DecimalEntry";
import RelatesToEntry from "./RelatesToEntry";
import ProfileEntry from "./ProfileEntry";
import { RecommendationJustificationComponentEntry } from "../components/RecommendationJustificationEdit";
import RelativeTimeEntry from "./RelativeTimeEntry";
import CodeableReferenceEntry from "./CodeableReferenceEntry";
import {
    EvidenceCertaintyEntry, EvidenceStatisticAttributeEstimateEntry, EvidenceStatisticEntry,
    EvidenceStatisticModelCharacteristicEntry, EvidenceStatisticModelCharacteristicVariableEntry
} from "../resourceComponents/editReferencedResourceComponents/EvidenceResourceEdit";
import ExtendedContactDetailEntry from "./ExtendedContactDetailEntry";
import AddressEntry from "./AddressEntry";
import HumanNameEntry from "./HumanNameEntry";
import { ListEntryEntry } from "../resourceComponents/ListResourceDisplay";
import { ActivityDefinitionDynamicValueEntry, ActivityDefinitionParticipantEntry } from "../resourceComponents/ActivityDefinitionResourceDisplay";
import { DocumentReferenceContentEntry } from "../resourceComponents/DocumentReferenceResourceDisplay";
import { DosageConditionEntry, DosageDetailsStepEntry, DosageEntry, DosageSafetyDoseLimitEntry, DoseAndRateEntry, R5DosageEntry, RatioEntry } from "./DosageEntry";
import { PlanDefinitionActionEntry, PlanDefinitionActorEntry, PlanDefinitionGoalEntry } from "../resourceComponents/PlanDefinitionResourceDisplay";
import { MeasureTermEntry } from "../resourceComponents/MeasureResourceDisplay";
import {
    EstimandEntry, EventHandlingEntry, ResearchStudyAssociatedPartyEntry,
    ResearchStudyComparisonGroupEntry, ResearchStudyLabelEntry, ResearchStudyObjectiveEntry,
    ResearchStudyObjectiveOutcomeMeasureEntry, ResearchStudyOutcomeMeasureEntry,
    ResearchStudyProgressStatusEntry, ResearchStudyRecruitmentEntry, ResearchStudyRegistrationExtensionEntry
} from "../resourceComponents/ResearchStudyResourceDisplay";
import {
    EvidenceVariableCategoryEntry, EvidenceVariableConstraintEntry, EvidenceVariableDataStorageEntry,
    EvidenceVariableDefinitionModifierEntry
} from "../resourceComponents/EvidenceVariableResourceDisplay";
import RelatedArtifactEntry from "./RelatedArtifactEntry";
import { EvidenceVariableDefinitionEntry } from "../resourceComponents/EvidenceResourceDisplay";
import { ConceptDesignationEntry, ValueSetComposeIncludeConceptEntry, ValueSetComposeIncludeEntry } from "../resourceComponents/ValueSetResourceDisplay";
import {
    CitationCitedArtifactContributorshipEntryEntry, CitationCitedArtifactContributorshipSummaryEntry,
    CitationClassificationEntry, CitationStatusDateEntry, CitationSummaryEntry, ContributionInstanceEntry
} from "../resourceComponents/CitationResourceDisplay";
import ExtensionEntry from "./ExtensionEntry";
import ExpressionEntry from "./ExpressionEntry";
import BooleanEntry from "./BooleanEntry";
import IntegerEntry from "./IntegerEntry";
import TimingEntry, { TimeEntry } from "./TimingEntry";
import UnsignedIntEntry from "./UnsignedIntEntry";
import { CodeSystemPropertyEntry } from "../resourceComponents/CodeSystemResourceDisplay";
import CodeArrayEntry from "./CodeArrayEntry";
import { ImplementationGuideDefinitionResourceEntry } from "../resourceComponents/ImplementationGuideResourceDisplay";
import { CodeSystemConceptDesignationEntry } from "../resourceComponents/CodeSystemTermEdit";
import ProfilesByResourceType from "../dictionaries/ProfilesByResourceType";
import addProfileValuesToNewResource from "../resourceComponents/profileFunctions/addProfileValuesToNewResource";
import { selectComparativeEvidenceMap } from "../slices/comparativeEvidenceMap/comparativeEvidenceMapSlice";
import submitFhirJson from "../functions/submitFhirJsonAPI";
import { resourceAdded } from "../slices/resourceCollection/resourceCollectionSlice";
import { ResearchSubjectSubjectMilestoneEntry, ResearchSubjectSubjectStateEntry } from "../resourceComponents/ResearchSubjectResourceDisplay";

const DataEntry = ({ datatype, elementName, fieldLabel, startingValue, setResourceState,
    startCollapsed, required, populationQuantity, outcomeCategories, outcomeHandling,
    allowedValues, allowedTypeValues, allowedContentTypes, allowedLanguages, dataEntryStyle,
    doNotShowFieldLabel, inTableCell, storeFalse, resourceType,
    setProfile, selectProfile, selectAnyProfile, addElementValues,
    valueSet, referencedResourceTypes, debug, deletable, enableCreation,
    valueSetForType, startingResourceType, preserveFOI,
    typeValueSet, typeCCValueSet, typeSpecificValueSets,
    systemChoices, setSystemChoices, systemChoicesOpen,
    extensionUrl, extensionValueType, codeableConceptLevelValueSet, foi, contentPath,
    relatesToClassifierValueSet,
    isStatisticModelProfile, isEndpointAnalysisPlanProfile, isParticipantFlowEvidenceProfile,
    isNetEffectContributionProfile, isNetEffectEstimateProfile, setTriggerReloadState }) => {
    switch (datatype) {
        case 'ActivityDefinitionDynamicValue':
            return <ActivityDefinitionDynamicValueEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'ActivityDefinitionParticipant':
            return <ActivityDefinitionParticipantEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Address':
            return <AddressEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Annotation':
            return <AnnotationEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Attachment':
            return <AttachmentEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} allowedContentTypes={allowedContentTypes} allowedLanguages={allowedLanguages} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Attester':
            return <AttesterEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'boolean':
            return <BooleanEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} storeFalse={storeFalse} inTableCell={inTableCell} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'canonical':
            return <UriEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'CitationCitedArtifactContributorshipEntry':
            return <CitationCitedArtifactContributorshipEntryEntry elementName={elementName}
                startingValue={startingValue} foi={foi}
                setResourceState={setResourceState} />
        case 'CitationCitedArtifactContributorshipSummary':
            return <CitationCitedArtifactContributorshipSummaryEntry elementName={elementName}
                startingValue={startingValue} foi={foi}
                setResourceState={setResourceState} />
        case 'CitationClassification':
            return <CitationClassificationEntry elementName={elementName}
                fieldLabel={fieldLabel} startingValue={startingValue}
                setResourceState={setResourceState} />
        case 'CitationStatusDate':
            return <CitationStatusDateEntry elementName={elementName}
                fieldLabel={fieldLabel} startingValue={startingValue}
                setResourceState={setResourceState} />
        case 'CitationSummary':
            return <CitationSummaryEntry elementName={elementName}
                fieldLabel={fieldLabel} startingValue={startingValue}
                setResourceState={setResourceState} />
        case 'code':
            return <CodeEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'CodeableConcept':
            return <CodeableConceptEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} allowedValues={allowedValues} deletable={deletable} debug={debug} systemChoices={systemChoices} systemChoicesOpen={systemChoicesOpen} startCollapsed={startCollapsed} codeableConceptLevelValueSet={codeableConceptLevelValueSet} />
        case 'CodeableReference':
            return <CodeableReferenceEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} systemChoices={systemChoices} systemChoicesOpen={systemChoicesOpen}
                setResourceState={setResourceState} valueSet={valueSet} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} allowedValues={allowedValues}
                deletable={deletable} debug={debug} referencedResourceTypes={referencedResourceTypes}
                enableCreation={enableCreation} startCollapsed={startCollapsed} />
        case 'CodeSystemProperty':
            return <CodeSystemPropertyEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                setResourceState={setResourceState} valueSet={valueSet} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} allowedValues={allowedValues} deletable={deletable} debug={debug} referencedResourceTypes={referencedResourceTypes} startCollapsed={startCollapsed} />
        case 'CodeSystemConceptDesignation':
            return <CodeSystemConceptDesignationEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                setResourceState={setResourceState} valueSet={valueSet} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} allowedValues={allowedValues} deletable={deletable} debug={debug} referencedResourceTypes={referencedResourceTypes} startCollapsed={startCollapsed} />
        case 'Coding':
            if (systemChoices) {
                return <CodingEntryWithSystemSelector elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                    setResourceState={setResourceState} systemChoices={systemChoices} setSystemChoices={setSystemChoices} systemChoicesOpen={systemChoicesOpen} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
            } else {
                return <CodingEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                    setResourceState={setResourceState} valueSet={valueSet} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
            }
        /**    TODO -- this may be used when creating Comment Builder
        case 'Comment':
        return <CommentEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
            setResourceState={setResourceState} allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
           */
        case 'ConceptDesignation':
            return <ConceptDesignationEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'ContactDetail':
            return <ContactDetailEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'ContactPoint':
            return <ContactPointEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'ContributionInstance':
            return <ContributionInstanceEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                setResourceState={setResourceState} deletable={deletable} startCollapsed={startCollapsed} />
        case 'date':
            return <DateEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'dateTime':
            return <DateTimeEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'decimal':
            return <DecimalEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'DocumentReferenceContent':
            return <DocumentReferenceContentEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Dosage':
            return <DosageEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet}
                debug={debug} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} startCollapsed={startCollapsed}
                allowedValues={allowedValues} deletable={deletable} />
        case 'R5Dosage':
            return <R5DosageEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet}
                debug={debug} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} startCollapsed={startCollapsed}
                allowedValues={allowedValues} deletable={deletable} />
        case 'DosageSafetyDoseLimit':
            return <DosageSafetyDoseLimitEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet}
                debug={debug} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} startCollapsed={startCollapsed}
                allowedValues={allowedValues} deletable={deletable} />
        case 'DosageDetailsStep':
            return <DosageDetailsStepEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet}
                debug={debug} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} startCollapsed={startCollapsed}
                allowedValues={allowedValues} deletable={deletable} />
        case 'DosageCondition':
            return <DosageConditionEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet}
                debug={debug} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} startCollapsed={startCollapsed}
                allowedValues={allowedValues} deletable={deletable} />
        case 'DoseAndRate':
            return <DoseAndRateEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet} debug={debug} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} allowedValues={allowedValues} deletable={deletable} startCollapsed={startCollapsed} />
        case 'EvidenceCertainty':
            return <EvidenceCertaintyEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'EvidenceStatistic':
            return <EvidenceStatisticEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                populationQuantity={populationQuantity} dataEntryStyle={dataEntryStyle}
                outcomeCategories={outcomeCategories} outcomeHandling={outcomeHandling}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState}
                deletable={deletable} debug={debug} startCollapsed={startCollapsed}
                isStatisticModelProfile={isStatisticModelProfile}
                isEndpointAnalysisPlanProfile={isEndpointAnalysisPlanProfile}
                isParticipantFlowEvidenceProfile={isParticipantFlowEvidenceProfile}
                isNetEffectContributionProfile={isNetEffectContributionProfile}
                isNetEffectEstimateProfile={isNetEffectEstimateProfile} />
        case 'EvidenceStatisticAttributeEstimate':
            return <EvidenceStatisticAttributeEstimateEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'EvidenceStatisticModelCharacteristic':
            return <EvidenceStatisticModelCharacteristicEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                populationQuantity={populationQuantity} dataEntryStyle={dataEntryStyle}
                outcomeCategories={outcomeCategories} outcomeHandling={outcomeHandling}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState}
                deletable={deletable} debug={debug} startCollapsed={startCollapsed}
                isStatisticModelProfile={isStatisticModelProfile}
                isEndpointAnalysisPlanProfile={isEndpointAnalysisPlanProfile}
                isParticipantFlowEvidenceProfile={isParticipantFlowEvidenceProfile}
                isNetEffectContributionProfile={isNetEffectContributionProfile}
                isNetEffectEstimateProfile={isNetEffectEstimateProfile} />
        case 'EvidenceStatisticModelCharacteristicVariable':
            return <EvidenceStatisticModelCharacteristicVariableEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                populationQuantity={populationQuantity} dataEntryStyle={dataEntryStyle}
                outcomeCategories={outcomeCategories} outcomeHandling={outcomeHandling}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState}
                deletable={deletable} debug={debug} startCollapsed={startCollapsed}
                isStatisticModelProfile={isStatisticModelProfile}
                isEndpointAnalysisPlanProfile={isEndpointAnalysisPlanProfile}
                isParticipantFlowEvidenceProfile={isParticipantFlowEvidenceProfile}
                isNetEffectContributionProfile={isNetEffectContributionProfile}
                isNetEffectEstimateProfile={isNetEffectEstimateProfile} />
        case 'EvidenceVariableCategory':
            return <EvidenceVariableCategoryEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'EvidenceVariableConstraint':
            return <EvidenceVariableConstraintEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'EvidenceVariableDataStorage':
            return <EvidenceVariableDataStorageEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'EvidenceVariableDefinition':
            return <EvidenceVariableDefinitionEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'EvidenceVariableDefinitionModifier':
            return <EvidenceVariableDefinitionModifierEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Expression':
            return <ExpressionEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'ExtendedContactDetail':
            return <ExtendedContactDetailEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Extension':
            return <ExtensionEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} extensionUrl={extensionUrl} extensionValueType={extensionValueType} />
        case 'HumanName':
            return <HumanNameEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Identifier':
            return <IdentifierEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet}
                preserveFOI={preserveFOI} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} startCollapsed={startCollapsed}
                allowedValues={allowedValues} deletable={deletable} />
        case 'ImplementationGuideDefinitionResource':
            return <ImplementationGuideDefinitionResourceEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                setResourceState={setResourceState} deletable={deletable} startCollapsed={startCollapsed} />
        case 'integer':
            return <IntegerEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'ListEntry':
            return <ListEntryEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed}
                referencedResourceTypes={referencedResourceTypes}
                startingResourceType={startingResourceType}
                setProfile={setProfile}
                selectProfile={selectProfile} />
        case 'MeasureTerm':
            return <MeasureTermEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                setResourceState={setResourceState} />
        case 'Period':
            return <PeriodEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} inTableCell={inTableCell} setResourceState={setResourceState}
                deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'PlanDefinitionAction':
            return <PlanDefinitionActionEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'PlanDefinitionActor':
            return <PlanDefinitionActorEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'PlanDefinitionGoal':
            return <PlanDefinitionGoalEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Profile':
            return <ProfileEntry elementName={elementName} startingValue={startingValue}
                resourceType={resourceType} setResourceState={setResourceState} />
        case 'positiveInt':
            return <PositiveIntEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Quantity':
            return <QuantityEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} valueSet={valueSet} startCollapsed={startCollapsed} deletable={deletable} />
        case 'Range':
            return <RangeEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} valueSet={valueSet} deletable={deletable} startCollapsed={startCollapsed} />
        /** 
                case 'Rating':
            return <RatingEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} deletable={deletable} typeValueSet={typeValueSet} typeSpecificValueSets={typeSpecificValueSets} startCollapsed={startCollapsed} />
          */
        case 'Ratio':
            return <RatioEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} valueSet={valueSet} deletable={deletable} startCollapsed={startCollapsed} />
        case 'RecommendationJustificationComponent':
            return <RecommendationJustificationComponentEntry elementName={elementName}
                fieldLabel={fieldLabel} startingValue={startingValue} foi={foi} contentPath={contentPath}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} typeValueSet={typeValueSet} typeCCValueSet={typeCCValueSet}
                typeSpecificValueSets={typeSpecificValueSets} startCollapsed={startCollapsed} />
        /** 
      case 'RecommendationJustificationContent':
      return <RecommendationJustificationContentEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} debug={debug} deletable={deletable} typeValueSet={typeValueSet} typeSpecificValueSets={typeSpecificValueSets} startCollapsed={startCollapsed} />
     */
        case 'Reference':
            return <ReferenceEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState}
                enableCreation={enableCreation} setProfile={setProfile} selectProfile={selectProfile} selectAnyProfile={selectAnyProfile}
                valueSet={valueSet} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} allowedValues={allowedValues} referencedResourceTypes={referencedResourceTypes}
                startingResourceType={startingResourceType} deletable={deletable} debug={debug} startCollapsed={startCollapsed}
                addElementValues={addElementValues} setTriggerReloadState={setTriggerReloadState} />
        case 'RelatesTo':
            return <RelatesToEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedTypeValues={allowedTypeValues} relatesToClassifierValueSet={relatesToClassifierValueSet}
                startCollapsed={startCollapsed} />
        case 'RelatedArtifact':
            return <RelatedArtifactEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState}
                valueSet={valueSet} debug={debug} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                allowedTypeValues={allowedTypeValues} referencedResourceTypes={referencedResourceTypes}
                deletable={deletable} startCollapsed={startCollapsed} />
        case 'RelativeTime':
            return <RelativeTimeEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState}
                doNotShowFieldLabel={doNotShowFieldLabel}
                valueSet={valueSet} debug={debug} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} referencedResourceTypes={referencedResourceTypes}
                deletable={deletable} startCollapsed={startCollapsed} />
        case 'ResearchStudyAssociatedParty':
            return <ResearchStudyAssociatedPartyEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'ResearchStudyComparisonGroup':
            return <ResearchStudyComparisonGroupEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'ResearchStudyLabel':
            return <ResearchStudyLabelEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'ResearchStudyObjective':
            return <ResearchStudyObjectiveEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'ResearchStudyObjectiveOutcomeMeasure':
            return <ResearchStudyObjectiveOutcomeMeasureEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'Estimand':
            return <EstimandEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'EventHandling':
            return <EventHandlingEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'ResearchStudyOutcomeMeasure':
            return <ResearchStudyOutcomeMeasureEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'ResearchStudyProgressStatus':
            return <ResearchStudyProgressStatusEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'ResearchStudyRecruitment':
            return <ResearchStudyRecruitmentEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                allowedValues={allowedValues} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
                debug={debug} deletable={deletable} startCollapsed={startCollapsed} />
        case 'ResearchStudyRegistrationExtension':
            return <ResearchStudyRegistrationExtensionEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                startCollapsed={startCollapsed} />
        case 'ResearchSubjectSubjectState':
            return <ResearchSubjectSubjectStateEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                startCollapsed={startCollapsed} />
        case 'ResearchSubjectSubjectMilestone':
            return <ResearchSubjectSubjectMilestoneEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState}
                startCollapsed={startCollapsed} />
        case 'string':
            return <StringEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} required={required} debug={debug} startCollapsed={startCollapsed} />
        /*
            case 'StudyAmendment':
          return <StudyAmendmentEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState}
            doNotShowFieldLabel={doNotShowFieldLabel}
            valueSet={valueSet} debug={debug} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} referencedResourceTypes={referencedResourceTypes}
            deletable={deletable} startCollapsed={startCollapsed} />
           */
        case 'time':
            return <TimeEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'Timing':
            return <TimingEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'unsignedInt':
            return <UnsignedIntEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'uri':
            return <UriEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        case 'UsageContext':
            return <UsageContextEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue}
                doNotShowFieldLabel={doNotShowFieldLabel} setResourceState={setResourceState} valueSet={valueSet} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} allowedValues={allowedValues} referencedResourceTypes={referencedResourceTypes} deletable={deletable} debug={debug} valueSetForType={valueSetForType} startCollapsed={startCollapsed} />
        case 'ValueSetComposeInclude':
            return <ValueSetComposeIncludeEntry elementName={elementName} fieldLabel={fieldLabel}
                startingValue={startingValue} setResourceState={setResourceState} deletable={deletable}
                debug={debug} startCollapsed={startCollapsed} />
        case 'ValueSetComposeIncludeConcept':
            return <ValueSetComposeIncludeConceptEntry elementName={elementName} fieldLabel={fieldLabel} startingValue={startingValue} setResourceState={setResourceState} deletable={deletable} debug={debug} startCollapsed={startCollapsed} />
        default:
            return <>OOPS!  DataEntry datatype NOT recognized!</>
    }
};

const deliverSearchResults = async (apiToken, modalState, selectedResourceTypes, setModalState) => {
    if (modalState.modalOpen && modalState.searchQueryString &&
        modalState.searchQueryCompleted && !modalState.searchResultsDelivered) {
        let searchResults = await getSearchResults(modalState.searchQueryString,
            null,
            selectedResourceTypes,
            null,
            [],
            apiToken);
        setModalState(prevState => {
            return { ...prevState, "searchResults": searchResults, "searchResultsDelivered": true }
        })
    }
};

const SelectMultipleResourcesFromSearchModal = ({ allowedResourceTypes,
    selectMultipleResourcesFromSearchState, setSelectMultipleResourcesFromSearchState }) => {

    const apiToken = useSelector(selectCurrentUserApiToken);
    let startingModalState = { "modalOpen": true, "searchQueryString": "", "searchResults": "" }

    const [modalState, setModalState] = useState(startingModalState);
    const [searchResourceTypeLimiterState, setSearchResourceTypeLimiterState] = useState({
        "selectedResourceTypes": allowedResourceTypes
    });

    useEffect((() => {
        deliverSearchResults(apiToken, modalState, searchResourceTypeLimiterState.selectedResourceTypes, setModalState);
    }), [modalState]);

    const selectTheResource = (data) => {
        let newArray = selectMultipleResourcesFromSearchState.selectedResourcesArray;
        newArray.push(data);
        setSelectMultipleResourcesFromSearchState(prevState => {
            return { ...prevState, "selectedResourcesArray": newArray };
        });
    }

    let modalContent = <div style={{
        paddingTop: "6px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        <Button style={{ padding: "6px", position: "absolute", right: "14px" }} className="formButton negative"
            content="✖"
            onClick={() => {
                setModalState(prevState => { return { ...prevState, modalOpen: false }; });
                setSelectMultipleResourcesFromSearchState(prevState => { return { ...prevState, modalOpen: false, applySelection: true }; });
            }} />
        <CodeArrayEntry elementName='selectedResourceTypes' fieldLabel='Limit by Resource Type'
            allowedValues={(allowedResourceTypes && allowedResourceTypes.length) ? allowedResourceTypes : allFhirResourceTypes} dataEntryStyle='dropdownsearch'
            startingValue={searchResourceTypeLimiterState.selectedResourceTypes || []}
            setResourceState={setSearchResourceTypeLimiterState} />
        <p style={{ paddingTop: "12px" }}>Limit to resource types of: {(searchResourceTypeLimiterState.selectedResourceTypes &&
            searchResourceTypeLimiterState.selectedResourceTypes.length > 0) ?
            searchResourceTypeLimiterState.selectedResourceTypes.join(', ') : "[No limits]"}</p>
        <br />
        <h3>Enter search string</h3>
        <TextField style={{ width: "100%", marginTop: "16px" }}
            className="inputField" type='text' label={"Enter search string"} size="small" variant='outlined'
            value={modalState.searchQueryString || ""}
            onKeyUp={(event) => {
                if (event.key === 'Enter') {
                    setModalState(prevState => { return { ...prevState, "searchQueryCompleted": true, "searchResultsDelivered": false } });
                }
            }}
            onChange={(e) => {
                setModalState(prevState => { return { ...prevState, "searchQueryString": e.target.value } })
            }} />
        {modalState.searchResults && <>
            <h3>Select resources</h3>
            <h4>{selectMultipleResourcesFromSearchState.selectedResourcesArray.length} resources selected. Click the x (top right corner), then click Update on the left panel, to complete.</h4>
            {modalState.searchResults.length > 0
                ?
                <>
                    {modalState.searchResults.map((result, resultIndex) => {
                        return <p key={resultIndex}>
                            <span style={{ cursor: "pointer" }}
                                onClick={() => {
                                    selectTheResource(result.data);
                                }} ><b>SELECT</b></span>
                            &nbsp;&nbsp;
                            <span >{result.display}</span>
                            &nbsp;&nbsp;
                            <a href={"https://fevir.net" + result.onClick} target='_blank' rel='noopener noreferrer' >View on FEvIR</a>
                        </p>
                    })}
                </>
                :
                <>No results found.</>}
        </>
        }
        <br />
    </div>;

    return <Modal
        style={{ padding: "0px", margin: "0px" }}
        dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
        open={modalState?.modalOpen}
        centered={false}
        content={modalContent}
    />;
};

const AddResourcesFromReferenceArrayEntryModal = ({ addResourcesModalState, setAddResourcesModalState }) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const combinedEvidenceMap = useSelector(selectComparativeEvidenceMap);

    const [profileSelectionState, setProfileSelectionState] = useState({});
    const [newResourceState, setNewResourceState] = useState({});

    useEffect(() => {
        let startingProfileSelectionState = { "profileChoices": [] };
        if (addResourcesModalState.startingResourceType &&
            (addResourcesModalState.setProfile || addResourcesModalState.selectAnyProfile ||
                addResourcesModalState.selectProfile)) {
            const profileDictionary = ProfilesByResourceType[addResourcesModalState.startingResourceType];
            const profileNames = Object.keys(profileDictionary);
            if (profileDictionary) {
                if (addResourcesModalState.setProfile) {
                    if (profileNames.includes(addResourcesModalState.setProfile)) {
                        let meta;
                        let section;
                        let content;
                        let profileUrl = "";
                        let citedArtifactClassificationToAdd;
                        let typeValue;
                        let categoryToAdd;
                        let useContextToAdd;
                        let membershipValue;
                        let actualValue;
                        let handlingValue;
                        let definitionValue;
                        let synthesisTypeValue;
                        let combinationMethodValue;
                        let characteristic;
                        let codeValue;
                        let additionalProfiles;
                        if (profileDictionary[addResourcesModalState.setProfile]) {
                            profileUrl = profileDictionary[addResourcesModalState.setProfile].url;
                            if (profileDictionary[addResourcesModalState.setProfile].citedArtifactClassificationToAdd) {
                                citedArtifactClassificationToAdd = profileDictionary[addResourcesModalState.setProfile].citedArtifactClassificationToAdd;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].typeValue) {
                                typeValue = profileDictionary[addResourcesModalState.setProfile].typeValue;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].categoryToAdd) {
                                categoryToAdd = profileDictionary[addResourcesModalState.setProfile].categoryToAdd;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].useContextToAdd) {
                                useContextToAdd = profileDictionary[addResourcesModalState.setProfile].useContextToAdd;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].membershipValue) {
                                membershipValue = profileDictionary[addResourcesModalState.setProfile].membershipValue;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].definitionValue) {
                                definitionValue = profileDictionary[addResourcesModalState.setProfile].definitionValue;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].actualValue === true ||
                                profileDictionary[addResourcesModalState.setProfile].actualValue === false) {
                                actualValue = profileDictionary[addResourcesModalState.setProfile].actualValue;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].handlingValue) {
                                handlingValue = profileDictionary[addResourcesModalState.setProfile].handlingValue;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].synthesisTypeValue) {
                                synthesisTypeValue = profileDictionary[addResourcesModalState.setProfile].synthesisTypeValue;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].combinationMethodValue) {
                                combinationMethodValue = profileDictionary[addResourcesModalState.setProfile].combinationMethodValue;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].codeValue) {
                                codeValue = profileDictionary[addResourcesModalState.setProfile].codeValue;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].sectionStarter) {
                                section = profileDictionary[addResourcesModalState.setProfile].sectionStarter;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].contentStarter) {
                                content = profileDictionary[addResourcesModalState.setProfile].contentStarter;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].characteristicStarter) {
                                characteristic = profileDictionary[addResourcesModalState.setProfile].characteristicStarter;
                            }
                            if (profileDictionary[addResourcesModalState.setProfile].additionalProfiles) {
                                additionalProfiles = profileDictionary[addResourcesModalState.setProfile].additionalProfiles;
                            }
                        }
                        if (profileUrl) {
                            meta = { "profile": [profileUrl] };
                            if (additionalProfiles) {
                                meta.profile = meta.profile.concat(additionalProfiles);
                            }
                            startingProfileSelectionState = {
                                "profileChoices": [addResourcesModalState.setProfile],
                                "profileDictionary": profileDictionary,
                                'meta': meta,
                                'citedArtifactClassificationToAdd': citedArtifactClassificationToAdd,
                                'section': section,
                                'content': content,
                                'characteristic': characteristic,
                                'typeValue': typeValue,
                                'categoryToAdd': categoryToAdd,
                                'useContextToAdd': useContextToAdd,
                                'membershipValue': membershipValue,
                                'definitionValue': definitionValue,
                                'actualValue': actualValue,
                                'handlingValue': handlingValue,
                                'synthesisTypeValue': synthesisTypeValue,
                                'combinationMethodValue': combinationMethodValue,
                                'codeValue': codeValue,
                                "profileSelected": addResourcesModalState.setProfile
                            };
                        }
                    }
                } else {
                    let profileChoices = [];
                    if (addResourcesModalState.selectAnyProfile) {
                        profileChoices = profileNames;
                    }
                    if (addResourcesModalState.selectProfile) {
                        for (const item of addResourcesModalState.selectProfile) {
                            if (profileNames.includes(item)) {
                                profileChoices.push(item);
                            }
                        }
                    }
                    if (profileChoices.length === 0) {
                        startingProfileSelectionState = { "profileSelected": "none" };
                    } else if (profileChoices.length === 1) {
                        startingProfileSelectionState = {
                            "profileChoices": profileChoices, "profileDictionary": profileDictionary,
                            "profileSelected": profileChoices[0]
                        };
                    } else if (profileChoices.length > 1) {
                        startingProfileSelectionState = {
                            "profileChoices": profileChoices,
                            "profileDictionary": profileDictionary
                        };
                    }
                }
            }
        }
        setProfileSelectionState(startingProfileSelectionState);
    }, []);

    useEffect(() => {
        let resourceType = addResourcesModalState.selectedResourceType || addResourcesModalState.startingResourceType;
        if (resourceType && profileSelectionState.profileSelected) {
            addProfileValuesToNewResource(resourceType, profileSelectionState.profileSelected, setNewResourceState, combinedEvidenceMap);
        }
    }, [profileSelectionState.profileSelected]);

    const addResources = async () => {
        if (addResourcesModalState.newTitleList?.length > 0) {
            let newReferenceArray = [];
            for (const title of addResourcesModalState.newTitleList) {
                let thisNewResourceState = JSON.parse(JSON.stringify(newResourceState));
                thisNewResourceState.title = title;
                let newResource = createNewReferencedResource(addResourcesModalState.selectedResourceType ||
                    addResourcesModalState.startingResourceType, thisNewResourceState, addResourcesModalState.addElementValues);
                let resourceFOI;
                let response = await submitFhirJson(newResource, title, apiToken);
                if (response?.success) {
                    resourceFOI = response.formstateid;
                    newResource.id = resourceFOI;
                    let resourceBody = {
                        id: resourceFOI,
                        displayTitle: title,
                        resourceType: newResource.resourceType,
                        resourceJson: newResource,
                        revisedResourceJson: newResource,
                        usageData: {
                            totalviews: 0,
                            totaledits: 1,
                            totalusersviewed: 0,
                            totalusersedited: 1,
                            editpermission: true,
                            readpermission: true,
                            adminpermission: true,
                            editors: [{ userid: userId, name: userName }],
                            status: "active"
                        }
                    }
                    dispatch(resourceAdded(resourceBody));
                    newReferenceArray.push({
                        "reference": newResource.resourceType + "/" + resourceFOI,
                        "type": newResource.resourceType,
                        "display": title
                    });
                } else {
                    alert("It did not work.");
                }
            }
            if (newReferenceArray.length > 0) {
                setAddResourcesModalState(prevState => {
                    return {
                        ...prevState,
                        "modalOpen": false,
                        "newReferenceArray": newReferenceArray
                    }
                })
            }
        }
    }

    let modalContent = <div style={{
        paddingTop: "6px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        <Button style={{ padding: "6px", position: "absolute", right: "14px" }}
            className="formButton negative"
            content="✖"
            onClick={() => {
                setAddResourcesModalState(prevState => { return { ...prevState, modalOpen: false }; });
            }} />
        {addResourcesModalState.referencedResourceTypes?.length > 0 ?
            <CodeEntry elementName='selectedResourceType' fieldLabel='Select Resource Type'
                startingValue={addResourcesModalState.startingResourceType || ""}
                setResourceState={setAddResourcesModalState}
                allowedValues={addResourcesModalState.referencedResourceTypes} />
            :
            <CodeEntry elementName='selectedResourceType' fieldLabel='Select Resource Type'
                startingValue={addResourcesModalState.startingResourceType || ""}
                allowedValues={allFhirResourceTypes} dataEntryStyle="dropdownsearch"
                setResourceState={setAddResourcesModalState} />
        }
        {(profileSelectionState.showProfileSelector && profileSelectionState.profileSelected !== "none") &&
            <SelectProfileForNewResource profileSelectionState={profileSelectionState}
                setProfileSelectionState={setProfileSelectionState} />}

        <br />
        {(addResourcesModalState.selectedResourceType || (addResourcesModalState.startingResourceType &&
            addResourcesModalState.referencedResourceTypes.length === 1
        )) ?
            <>
                <h3>Enter titles to create new {(profileSelectionState.profileSelected &&
                    profileSelectionState.profileSelected !== "none") ?
                    profileSelectionState.profileSelected
                    :
                    addResourcesModalState.selectedResourceType ?
                        addResourcesModalState.selectedResourceType
                        :
                        addResourcesModalState.startingResourceType
                } Resources</h3>
                <ArrayEntry datatype="string" elementName="newTitleList"
                    fieldLabel={"Title"}
                    startingValue={addResourcesModalState.newTitleList || []}
                    setResourceState={setAddResourcesModalState} />
            </>
            :
            <h3>You must select a Resource Type before creating new Resources.</h3>
        }
        <br />
        <Button className="formButton" style={{ color: "#FFFFFF" }}
            positive={true}
            content={`Create the ${addResourcesModalState.setProfile ||
                profileSelectionState?.profileSelected || addResourcesModalState.selectedResourceType ||
                addResourcesModalState.startingResourceType || "[MUST SELECT A TYPE FIRST]"} Resources`}
            onClick={() => {
                addResources();
            }
            }
            disabled={!(addResourcesModalState.selectedResourceType ||
                (addResourcesModalState.startingResourceType &&
                    addResourcesModalState.referencedResourceTypes.length === 1)
            )} />
    </div>;

    return <Modal
        style={{ padding: "0px", margin: "0px" }}
        dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
        open={addResourcesModalState?.modalOpen}
        centered={false}
        content={modalContent}
    />;
};

const calculateStartingArrayAsObject = (startingValue) => {
    let startingArrayAsObject = {};
    if (startingValue?.length > 0) {
        let startingValueCopy = JSON.parse(JSON.stringify(startingValue));
        for (let itemIndex in startingValueCopy) {
            startingArrayAsObject[itemIndex] = startingValueCopy[itemIndex];
        }
    } else {
        startingArrayAsObject = { "0": null };
    }
    return startingArrayAsObject;
};

const ArrayEntry = ({ datatype, elementName, fieldLabel, startingValue = "", setResourceState,
    addElementValues, allowedValues, codeableConceptLevelValueSet,
    populationQuantity, outcomeCategories, outcomeHandling, preserveFOI,
    allowedTypeValues, allowedContentTypes, allowedLanguages, dataEntryStyle, inTableCell, valueSet, referencedResourceTypes,
    debug, deletable, deletableArray, doNotShowFieldLabel, typeValueSet, typeCCValueSet,
    classifierValueSet, systemChoices, setSystemChoices, systemChoicesOpen,
    startEmptyArrayClosed, typeSpecificValueSets, startCollapsed, startingResourceType, extensionUrl,
    extensionValueType, classificationProfile, enableCreation, resourceType, setProfile, selectProfile,
    selectAnyProfile, foi, contentPath, relatesToClassifierValueSet,
    isStatisticModelProfile, isEndpointAnalysisPlanProfile, isParticipantFlowEvidenceProfile,
    isNetEffectContributionProfile, isNetEffectEstimateProfile, setTriggerReloadState }) => {

    const [arrayState, setArrayState] = useState(null);
    const [startingArrayState, setStartingArrayState] = useState(null);
    const [undoArrayState, setUndoArrayState] = useState(null);
    const [showDataEntryState, setShowDataEntryState] = useState(true);
    const [selectMultipleResourcesFromSearchState, setSelectMultipleResourcesFromSearchState] = useState({
        "modalOpen": false, "selectedResourcesArray": []
    });
    const [addResourcesModalState, setAddResourcesModalState] = useState({ "modalOpen": false });
    const [remount, setRemount] = useState(0);

    useEffect(() => {
        const startingArrayAsObject = calculateStartingArrayAsObject(startingValue);
        setArrayState(startingArrayAsObject);
        setStartingArrayState(startingArrayAsObject);
        if (!startingValue && startEmptyArrayClosed) {
            setShowDataEntryState(false);
        }
    }, []);

    useEffect(() => {
        if (selectMultipleResourcesFromSearchState.selectedResourcesArray.length > 0 &&
            selectMultipleResourcesFromSearchState.applySelection) {
            let initialArray = startingValue || [];
            let newArray = initialArray.concat(selectMultipleResourcesFromSearchState.selectedResourcesArray);
            setResourceState(prevState => { return { ...prevState, [elementName]: newArray } });
        }
    }, [selectMultipleResourcesFromSearchState]);

    useEffect((() => {
        //console.log("ArrayEntry useEffect due to change in arrayState")
        if (arrayState && Object.keys(arrayState).length > 0 &&
            JSON.stringify(startingArrayState) !== JSON.stringify(arrayState)) {
            let newArray = [];
            let additionInProgress = false;
            for (let key of Object.keys(arrayState)) {
                if (arrayState[key] === null || arrayState[key] === "") {
                    additionInProgress = true;
                }
                if (arrayState[key] !== null &&
                    !(typeof arrayState[key] === "object" && Object.keys(arrayState[key]).length === 0) &&
                    !(Array.isArray(arrayState[key]) && arrayState[key].length === 0) &&
                    arrayState[key] !== "" &&
                    arrayState[key] !== undefined && arrayState[key] !== "DELETEME") {
                    newArray.push(arrayState[key]);
                }
            }
            if (!additionInProgress) {
                if (newArray.length > 0) {
                    setResourceState(prevState => { return { ...prevState, [elementName]: newArray } });
                } else {
                    setResourceState(prevState => { return { ...prevState, [elementName]: null } });
                }
                if (undoArrayState && (Object.keys(arrayState).length !== 1 || (Object.keys(arrayState).length === 1 && arrayState['0'] !== "DELETEME"))) {
                    setUndoArrayState(null);
                }
            }
        }
    }), [arrayState]);

    useEffect(() => {
        if (undoArrayState) {
            setArrayState({ '0': "DELETEME" });
        }
    }, [undoArrayState]);

    useEffect(() => {
        if (addResourcesModalState.newReferenceArray?.length > 0) {
            let newArray = [];
            if (startingValue?.length > 0) {
                newArray = startingValue.concat(addResourcesModalState.newReferenceArray);
            } else {
                newArray = addResourcesModalState.newReferenceArray;
            }
            let newArrayAsObject = calculateStartingArrayAsObject(newArray);
            setArrayState(newArrayAsObject);
            setRemount(prevState => { return (prevState + 1); });
            //  setStartingArrayState(newArrayAsObject);
            //      setResourceState(prevState => { return { ...prevState, [elementName]: newArray } });
        }
    }, [addResourcesModalState.newReferenceArray]);

    return <>
        {(arrayState && Object.keys(arrayState).length > 0) &&
            <div>
                {(showDataEntryState === false || doNotShowFieldLabel ||
                    (inTableCell && arrayState && Object.entries(arrayState).length > 0))
                    ? <></> :
                    <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>}
                {(datatype === "Reference" && enableCreation === true) && <>
                    <br />
                    <Button className="formButton" style={{ color: "#000000" }}
                        content={"Create NEW " + (setProfile || startingResourceType || "") + " Resources"}
                        onClick={() => {
                            setAddResourcesModalState({
                                "modalOpen": true, "setProfile": setProfile, "selectProfile": selectProfile,
                                "selectAnyProfile": selectAnyProfile, "startingResourceType": startingResourceType,
                                "referencedResourceTypes": referencedResourceTypes,
                                "addElementValues": addElementValues
                            });
                        }}
                    />
                </>}
                {addResourcesModalState.modalOpen &&
                    <AddResourcesFromReferenceArrayEntryModal
                        addResourcesModalState={addResourcesModalState}
                        setAddResourcesModalState={setAddResourcesModalState} />}
                {showDataEntryState === false &&
                    <Button className="formButton" style={{ color: "#000000", marginBottom: "12px" }}
                        content={"+ Add " + fieldLabel}
                        onClick={() => {
                            setArrayState({ "0": null });
                            setShowDataEntryState(true);
                        }} />}
                <div style={{ marginLeft: (doNotShowFieldLabel || inTableCell) ? "0px" : "24px" }}>
                    {(arrayState && showDataEntryState === true) &&
                        Object.entries(arrayState).map((keyValuePair, keyValuePairIndex) => {
                            return <div key={keyValuePairIndex}
                                style={{ marginBottom: doNotShowFieldLabel ? "6px" : "0px" }}>
                                {keyValuePair[1] === "DELETEME" ?
                                    <>
                                        <p style={{ marginBottom: "0px" }}><b>{fieldLabel + ' entry ' + (keyValuePairIndex + 1)}: </b></p>
                                        <div style={{ marginLeft: "24px" }}>WILL BE DELETED.</div>
                                    </>
                                    :
                                    <>
                                        <DataEntry key={remount + "-" + keyValuePairIndex}
                                            datatype={datatype} elementName={keyValuePair[0]}
                                            enableCreation={enableCreation} preserveFOI={preserveFOI}
                                            doNotShowFieldLabel={true} resourceType={resourceType}
                                            codeableConceptLevelValueSet={codeableConceptLevelValueSet}
                                            populationQuantity={populationQuantity} outcomeCategories={outcomeCategories}
                                            outcomeHandling={outcomeHandling}
                                            fieldLabel={doNotShowFieldLabel ||
                                                Object.entries(arrayState).length > 1 ? fieldLabel + ' entry ' + (keyValuePairIndex + 1) : fieldLabel}
                                            classificationProfile={classificationProfile}
                                            startingValue={keyValuePair[1]}
                                            setResourceState={setArrayState} allowedValues={allowedValues}
                                            dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} valueSet={valueSet}
                                            referencedResourceTypes={referencedResourceTypes}
                                            debug={debug} allowedContentTypes={allowedContentTypes} allowedLanguages={allowedLanguages}
                                            startCollapsed={(keyValuePair[1] && keyValuePair[1] !== null) && startCollapsed}
                                            startingResourceType={startingResourceType}
                                            systemChoices={systemChoices} setSystemChoices={setSystemChoices} systemChoicesOpen={systemChoicesOpen}
                                            typeSpecificValueSets={typeSpecificValueSets}
                                            addElementValues={addElementValues}
                                            allowedTypeValues={allowedTypeValues} deletable={deletable} typeValueSet={typeValueSet}
                                            typeCCValueSet={typeCCValueSet} foi={foi} contentPath={contentPath}
                                            classifierValueSet={classifierValueSet} extensionUrl={extensionUrl} extensionValueType={extensionValueType}
                                            setProfile={setProfile} selectProfile={selectProfile} selectAnyProfile={selectAnyProfile}
                                            relatesToClassifierValueSet={relatesToClassifierValueSet}
                                            isStatisticModelProfile={isStatisticModelProfile}
                                            isEndpointAnalysisPlanProfile={isEndpointAnalysisPlanProfile}
                                            isParticipantFlowEvidenceProfile={isParticipantFlowEvidenceProfile}
                                            isNetEffectContributionProfile={isNetEffectContributionProfile}
                                            isNetEffectEstimateProfile={isNetEffectEstimateProfile} setTriggerReloadState={setTriggerReloadState} />
                                    </>
                                }
                                {deletable && <span style={{ marginLeft: "24px" }}>
                                    &nbsp;&nbsp;
                                    <Button className="formButton" style={{ color: "#000000" }}
                                        content={"x Delete " + (doNotShowFieldLabel ||
                                            (arrayState && Object.entries(arrayState).length > 1)
                                            ?
                                            fieldLabel + ' entry ' + (keyValuePairIndex + 1) : fieldLabel)}
                                        onClick={() => {
                                            setArrayState(prevState => {
                                                return {
                                                    ...prevState,
                                                    [keyValuePairIndex.toString()]: "DELETEME"
                                                };
                                            });
                                        }} />
                                    <br /><br />
                                </span>}
                            </div>
                        })}
                    <div style={{ marginBottom: "12px" }}>
                        {(arrayState && showDataEntryState === true) &&
                            <>
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content={"+ Add " + fieldLabel}
                                    onClick={() => {
                                        setArrayState(prevState => {
                                            let arrayLength = Object.keys(prevState).length;
                                            let arrayIndexNumber = arrayLength.toString();
                                            if (arrayLength === 0) {
                                                return { "0": "" };
                                            }
                                            return {
                                                ...prevState,
                                                [arrayIndexNumber]: null
                                            };
                                        });
                                        if (!showDataEntryState) {
                                            setShowDataEntryState(true);
                                        }
                                    }} />
                            </>}
                        {datatype === "Reference" && <><br />
                            <Button className="formButton" style={{ color: "#000000" }}
                                content={"Search Resources to Add Multiple " + fieldLabel + " Entries"}
                                onClick={() => { setSelectMultipleResourcesFromSearchState({ "modalOpen": true, "selectedResourcesArray": [] }); }}
                            />
                        </>}
                        {selectMultipleResourcesFromSearchState.modalOpen &&
                            <SelectMultipleResourcesFromSearchModal allowedResourceTypes={referencedResourceTypes}
                                selectMultipleResourcesFromSearchState={selectMultipleResourcesFromSearchState}
                                setSelectMultipleResourcesFromSearchState={setSelectMultipleResourcesFromSearchState} />}
                        {(deletableArray && showDataEntryState === true && arrayState && Object.keys(arrayState).length !== 0) &&
                            <Button className="formButton" style={{ color: "#000000" }} content={"Delete All " + fieldLabel + " Entries"}
                                onClick={() => {
                                    alert('Deletion will remove all data in this list. When you click Update, the data will be deleted. If you do not want to delete, refresh the page to lose all your edits instead of clicking Update.');
                                    setUndoArrayState(arrayState);
                                    //setArrayState({ '0': null });
                                }} />}
                        {undoArrayState && <Button className="formButton" style={{ color: "#000000", marginLeft: "2px" }}
                            content={"Undo Deletion"}
                            onClick={() => { setArrayState(undoArrayState); }} />}
                    </div>
                </div>
            </div>
        }
    </>
};

export default ArrayEntry;
export { DataEntry };